import React from "react";

const Pagination = ({ currentPage, totalPages, onPageChange, totalDocs, totalItems }: {
  currentPage: number;
  totalPages: number;
  totalDocs?: number;
  totalItems?: number
  onPageChange: (page: number) => void;
}) => {
  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5; // Show max 5 page numbers at once
    
    // Always show page 1
    pageNumbers.push(
      <button
        key={1}
        onClick={() => onPageChange(1)}
        className={`w-8 h-8 flex items-center justify-center rounded-md ${
          currentPage === 1 
            ? "bg-white text-green-600 font-medium" 
            : "text-gray-600 hover:bg-gray-50"
        }`}
      >
        1
      </button>
    );

    // Calculate range to display
    let startPage = Math.max(2, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);
    
    // Adjust to show up to maxPagesToShow pages
    if (endPage - startPage < maxPagesToShow - 2) {
      if (currentPage < totalPages / 2) {
        endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 2);
      } else {
        startPage = Math.max(2, endPage - (maxPagesToShow - 2));
      }
    }

    if (startPage > 2) {
      pageNumbers.push(
        <span key="start-ellipsis" className="w-8 h-8 flex items-center justify-center">
          ...
        </span>
      );
    }

   
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => onPageChange(i)}
          className={`w-8 h-8 flex items-center justify-center rounded-md ${
            currentPage === i 
              ? "bg-white text-green-600 font-medium" 
              : "text-gray-600 hover:bg-gray-50"
          }`}
        >
          {i}
        </button>
      );
    }

    
   
    if (endPage < totalPages - 1) {
      pageNumbers.push(
        <span key="end-ellipsis" className="w-8 h-8 flex items-center justify-center">
          ...
        </span>
      );
    }


    if (totalPages > 1) {
      pageNumbers.push(
        <button
          key={totalPages}
          onClick={() => onPageChange(totalPages)}
          className={`w-8 h-8 flex items-center justify-center rounded-md ${
            currentPage === totalPages 
              ? "bg-white text-green-600 font-medium" 
              : "text-gray-600 hover:bg-gray-50"
          }`}
        >
          {totalPages}
        </button>
      );
    }

    return pageNumbers;
  };

  return (
    <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-white">
      <div className="text-sm text-gray-500">
        Showing {totalItems} of {totalDocs } results
      </div>

      <div className="flex items-center space-x-2">
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          className={`flex items-center px-3 py-1 text-sm font-medium rounded-md ${
            currentPage === 1
              ? "text-gray-300 cursor-not-allowed"
              : "text-gray-700 hover:bg-gray-50"
          }`}
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-4 w-4 mr-1" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path 
              fillRule="evenodd" 
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" 
              clipRule="evenodd" 
            />
          </svg>
          Previous
        </button>

        <div className="flex items-center space-x-1">
          {renderPageNumbers()}
        </div>

        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className={`flex items-center px-3 py-1 text-sm font-medium rounded-md ${
            currentPage === totalPages
              ? "text-gray-300 cursor-not-allowed"
              : "text-gray-700 hover:bg-gray-50"
          }`}
        >
          Next
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-4 w-4 ml-1" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path 
              fillRule="evenodd" 
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" 
              clipRule="evenodd" 
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Pagination;